import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  CohortSummaryAttributeResult,
  CohortCharacteristicsResultType,
} from '@verantos-dev/types-cohorts';

import { calculateAxisData, generateRangeArray } from '../helpers';
import { BarChart } from './BarChart';

export type AgeAtIndexDistributionChartProps = {
  data: CohortSummaryAttributeResult[] | undefined;
  resultType?: CohortCharacteristicsResultType;
  'data-testid'?: string;
};

export const AgeAtIndexDistributionChart = ({
  data,
  resultType = CohortCharacteristicsResultType.AGE_AT_INDEX,
  'data-testid': dataTestId,
}: AgeAtIndexDistributionChartProps) => {
  const intl = useIntl();
  const theme = useTheme();

  if (!data) return null;

  // generating range for xAxis labels of cohort characteristics age chart
  const barSteps = generateRangeArray(0, 100, 5);
  // mapping data with xAxis steps
  const axisData = calculateAxisData(barSteps, data);

  return (
    <BarChart
      data={axisData}
      series={[
        {
          label: (location: string) =>
            location === 'tooltip'
              ? intl.formatMessage({
                  id: 'projects.cohortBuilder.cohortCharacteristicsCharts.barChart.seriesLabel',
                })
              : '',
          dataKey: 'attributeCount',
          color: theme.palette.tertiary.darkBlue,
          valueFormatter: (item: any, context: any) => {
            const attributeName = axisData[context.dataIndex].attributeName;
            const attributeCount = axisData[context.dataIndex].attributeCount.toLocaleString();
            const attributePercentage = axisData[context.dataIndex].attributePercentage || 0;
            return `${attributeName}\u00A0\u00A0${attributeCount}  (${attributePercentage}%)`;
          },
        },
      ]}
      xAxis={[
        {
          label: intl.formatMessage({
            id: 'projects.cohortBuilder.cohortCharacteristicsCharts.barChart.xAxisLabel',
          }),
          labelStyle: { fontWeight: 500 },
          scaleType: 'band',
          tickLabelInterval: () => true,
          dataKey: 'attributeName',
          valueFormatter: (ageRange: string) => {
            const startRange = ageRange.split('-')[0];
            return startRange;
          },
          tickPlacement: 'start',
          tickLabelPlacement: 'tick',
          hideTooltip: true,
        },
      ]}
      yAxis={[
        {
          label: intl.formatMessage({
            id: 'projects.cohortBuilder.cohortCharacteristicsCharts.barChart.yAxisLabel',
          }),
          dataKey: 'attributeCount',
          labelStyle: { transform: 'rotate(0deg) translateX(-30px)', fontWeight: 500 },
          hideTooltip: true,
        },
      ]}
      slotProps={{
        legend: { hidden: true },
        popper: {
          sx: {
            '& .MuiChartsTooltip-labelCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              '& .MuiTypography-root': {
                color: `${theme.palette.text.secondary}  !important`,
              },
            },
            '& .MuiChartsTooltip-valueCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              paddingLeft: '5px !important',
              '& .MuiTypography-root': {
                color: `${theme.palette.text.primary}  !important`,
              },
            },
          },
        },
      }}
      data-testid={dataTestId || `cohortCharacteristic-graph-bar-chart-${resultType}`}
    />
  );
};
